import { FC } from 'react';
import InvestmentPerformance from './CustomDashboards/InvestmentPerformance';
import FundingDetailsAdmin from '../Admin/RequestTracker/FundingDetailsAdmin';
import FundingDetails from './CustomDashboards/FundingDetails';
import Benchmarks from './CustomDashboards/Benchmarks';
import FinancialProjections from './CustomDashboards/FinancialProjections';
import GamePerformance from './CustomDashboards/GamePerformance';
import FundingEligibility from './CustomDashboards/FundingEligibility';
import RequestableDashboard from './RequestableDashboard';
import { REQUESTED_SUBMODULES_META } from '@constants/constants';
import { MODULES_REQUEST_STATUS } from '@utils/types/RequestedDashboards';
import DealSetup from '../Admin/DealSetup/DealSetup';
import ReferrerSetup from '../Admin/Referrer/ReferrerSetup';
import AdImpressions from './CustomDashboards/AdImpressions';
import AccountingRevenue from './CustomDashboards/AccountingRevenue';
import GettingStarted from './CustomDashboards/GettingStarted';
import GettingStartedDemo from './CustomDashboards/GettingStartedDemo';
import UnderwrittingPreview from './GettingStartedDashboards/GettingStartedUnderwriting';
import FundingEligibilityPreview from './GettingStartedDashboards/GettingStartedFundingEligibility';
import BenchmarksPreview from './GettingStartedDashboards/GettingStartedBenchmarks';
import FinancialProjectionsPreview from './GettingStartedDashboards/GettingStartedFinancialProjections';
import FinancialProjectionsPreviewDemo from '../Demo/GettingStartedDashboards/GettingStartedFinancialProjections';
import CommissionsDashboard from './CustomDashboards/CommissionsDashboard';
import UnderwrittingDashboard from './CustomDashboards/UnderwrittingDashboard';
import DownloadReports from './CustomDashboards/DownloadReports';

interface CustomDashboardWrapperProps {
  data: {
    id: string;
    name?: string;
    embedId?: string;
    submoduleType?: string;
    filterQuery?: string;
    requestStatus: MODULES_REQUEST_STATUS;
    requestId?: string;
    isPreviewEnabled?: boolean;
  } | null;
}

const dashboardComponents: Record<string, FC<{ id: string; role?: 'ADMIN' | 'USER' }>> = {
  INVESTMENT_PERFORMANCE: InvestmentPerformance,
  FUNDING_REQUEST: FundingDetails,
  FUNDING_REQUEST_TRACKER: FundingDetailsAdmin,
  FINANCIAL_PROJECTIONS: FinancialProjections,
  FINANCIAL_PROJECTIONS_V3: FinancialProjections,
  FINANCIAL_PROJECTIONS_V2: FinancialProjections,
  GAME_PERFORMANCE: GamePerformance,
  BENCHMARKS_CUSTOM: Benchmarks,
  FUNDING_ELIGIBILITY: FundingEligibility,
  DEAL_SETUP: DealSetup,
  AD_IMPRESSIONS: AdImpressions,
  ACCOUNTING_REVENUE: AccountingRevenue,
  GETTING_STARTED: GettingStarted,
  GETTING_STARTED_DEMO: GettingStartedDemo,
  COMMISSIONS_DASHBOARD: CommissionsDashboard,
  DOWNLOADABLE_REPORTS: DownloadReports,
  REFERRER_SETUP: ReferrerSetup,
  COHORT_ANALYSIS: UnderwrittingDashboard,
};

const previewDashboardComponents: Record<string, FC> = {
  UNDERWRITTING: UnderwrittingPreview,
  FUNDING_ELIGIBILITY: FundingEligibilityPreview,
  BENCHMARKS: BenchmarksPreview,
  FINANCIAL_PROJECTIONS: FinancialProjectionsPreview,
  FINANCIAL_PROJECTIONS_DEMO: FinancialProjectionsPreviewDemo,
};

const CustomDashboardWrapper: FC<CustomDashboardWrapperProps> = ({ data }) => {
  if (!data?.submoduleType || !data.id) {
    return null;
  }

  if (data.requestStatus !== 'APPROVED') {
    return (
      <RequestableDashboard
        title={REQUESTED_SUBMODULES_META[data.submoduleType].title}
        subTitle={REQUESTED_SUBMODULES_META[data.submoduleType].subTitle}
        body={REQUESTED_SUBMODULES_META[data.submoduleType].body}
        requestStatus={data.requestStatus}
        images={REQUESTED_SUBMODULES_META[data.submoduleType].images}
        requestId={data.requestId as string}
      />
    );
  }

  const DashboardComponent = dashboardComponents[data.submoduleType];
  const PreviewDashboardComponent = previewDashboardComponents[data.submoduleType];

  if (!!data.isPreviewEnabled) {
    return <PreviewDashboardComponent />;
  }

  return DashboardComponent ? <DashboardComponent id={data.id} role={'ADMIN'} /> : <></>;
};

export default CustomDashboardWrapper;
